import React, { useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { ChevronLeft } from "lucide-react";
import Profile from "../../../../assets/images/icons/icons8-male-user-50.png";
import ScrollableTabsContainer from "./ScrollableTabsContainer";
import PropTypes from "prop-types";
import Dependents from "./Dependents";
import NationalID from "./NationaID";
import PersonalNew from "./PersonalNew";
import EmploymentInformationNew from "./EmploymentInformationNew";
import IqamaNew from "./IqamaNew";
import SalaryNew from "./SalaryNew";
import AccountNew from "./AccountNew";
import HealthInsuranceNew from "./HealthInsuranceNew";
import HealthCardNew from "./HealthCardNew";
import PassportNew from "./PassportNew";
import DrivingLicenseNew from "./DrivingLicenseNew";
import ContractDetailsNew from "./ContractDetailsNew";
import SubscriptionNew from "./SubscriptionNew";
import VisaNew from "./VisaNew";
import Medical from "./Medical";
import Travels from "./Travels";
import Other from "./Other";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";

const EmployeeDetails = ({
  isOpen,
  onClose,
  onBack,
  personalInfo,
  employementInfo,
  nationalIdDetails,
  salaryDetails,
  iqamaDetails,
  accountDetails,
  healthInsuranceDetails,
  healthCardDetails,
  passportDetails,
  visaDetails,
  contractDetails,
  drivingLicenseDetails,
  subscriptionDetails,
  dependentsInfo,
  employeeGeneralInfo,
  empPublicId,
  getEmployeeRegistrationData,
  handleGetTableData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("Personal");

  const renderTabContent = () => {
    switch (activeTab) {
      case "Personal":
        return (
          <PersonalNew
            personalInfo={personalInfo}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "Employment Information":
        return (
          <EmploymentInformationNew
            personalInfo={personalInfo}
            employmentInfo={employementInfo}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "National ID":
        return (
          <NationalID
            personalInfo={personalInfo}
            nationalDetails={nationalIdDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
          />
        );
      case "Iqama":
        return (
          <IqamaNew
            personalInfo={personalInfo}
            iqamaDetails={iqamaDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "Salary":
        return (
          <SalaryNew
            personalInfo={personalInfo}
            salaryDetails={salaryDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "Account":
        return (
          <AccountNew
            personalInfo={personalInfo}
            accountDetails={accountDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "Medical Information":
        return (
          <Medical
            personalInfo={personalInfo}
            healthInsuranceDetails={healthInsuranceDetails}
            healthCardDetails={healthCardDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );
      case "Immigration Information":
        return (
          <Travels
            personalInfo={personalInfo}
            passportDetails={passportDetails}
            visaDetails={visaDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );

      case "Dependents":
        return (
          <Dependents
            personalInfo={personalInfo}
            dependentsInfo={dependentsInfo}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
          />
        );
      case "Other Information":
        return (
          <Other
            personalInfo={personalInfo}
            visaDetails={visaDetails}
            contractDetails={contractDetails}
            drivingLicenseDetails={drivingLicenseDetails}
            subscriptionDetails={subscriptionDetails}
            handleGetTableData={handleGetTableData}
            empPublicId={empPublicId}
            getEmployeeRegistrationData={getEmployeeRegistrationData}
            currentEmployeeEditing={currentEmployeeEditing}
          />
        );

      default:
        return null;
    }
  };

  const tabs = [
    "Personal",
    "Employment Information",
    "Medical Information",
    "Immigration Information",
    ...(personalInfo?.citizen === "true" ? ["National ID"] : []),
    ...(personalInfo?.citizen === "false" ? ["Iqama"] : []),
    "Salary",
    "Account",
    "Dependents",
    "Other Information",
  ];

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-[9999]">
        <div className="fixed inset-0 bg-[#04080B59]" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full p-2 items-start justify-center sm:items-center sm:justify-end p-0 sm:p-4">
            <DialogPanel className="w-full h-screen sm:h-auto sm:max-h-[90vh] sm:max-w-[90%] md:max-w-[800px] bg-[#F2F4F7] rounded-none sm:rounded-xl overflow-y-auto scrollbar-hide transform sm:translate-y-0">
              {/* Header */}
              <div className="flex items-center bg-white border-[1px] border-[#EAECF0] p-2 sm:p-4">
                <button
                  onClick={onBack}
                  className="flex items-center gap-1 sm:gap-2 text-black hover:text-gray-800 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7 m-1 sm:m-2 bg-white rounded-md" />
                  <span className="font-bold text-lg sm:text-xl">
                    {t("Employee Profile")}
                  </span>
                </button>
              </div>

              <div className="p-2 sm:p-4">
                <p className="text-[#475467] text-sm sm:text-base mb-4 sm:mb-6">
                  {t(
                    `Here You Can View, Edit, And Update Employee Profiles Effortlessly. Keep Information Up-To-Date And Manage Employee Details Seamlessly.`
                  )}
                </p>

                {/* Profile Header */}
                <div className="bg-[#1A6D73] p-3 sm:p-6 rounded-lg sm:rounded-xl mb-4 sm:mb-6">
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-2 sm:mb-4">
                    <img
                      src={personalInfo?.profilePicture?.fileUrl || Profile}
                      alt="Profile"
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full"
                    />
                    <div className="flex-1 min-w-0">
                      <h2 className="text-white text-lg sm:text-xl font-semibold truncate">
                        {`${personalInfo?.firstName} ${
                          personalInfo?.secondName || ""
                        } ${personalInfo?.thirdName || ""} ${
                          personalInfo?.lastName
                        }`}
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 py-1 sm:py-2 text-[#CDF1F4]">
                        <span className="bg-[#0E474B] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                          {employementInfo?.employeeId}
                        </span>
                        <span className="bg-[#0E474B] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                          {employementInfo?.roleInfo?.role}
                        </span>
                        <span className="bg-[#0E474B] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                          {personalInfo?.citizen === "true"
                            ? t("Saudi")
                            : t("Non-Saudi")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs - Fixed spacing */}
                <div className="border-b border-[#EAECF0] bg-white h-10">
                  <ScrollableTabsContainer>
                    <div className="flex min-w-max">
                      {tabs.map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap ${
                            activeTab === tab
                              ? "text-[#1A6D73] border-b-2 bg-[#E8F0F1] border-[#1A6D73]"
                              : "text-[#475467]"
                          }`}
                        >
                          {t(tab)}
                        </button>
                      ))}
                    </div>
                  </ScrollableTabsContainer>
                </div>

                {/* Tab Content */}
                <div className="mt-2 sm:mt-4">{renderTabContent()}</div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
};

EmployeeDetails.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  personalInfo: PropTypes.object,
  employementInfo: PropTypes.object,
  nationalIdDetails: PropTypes.object,
  salaryDetails: PropTypes.object,
  iqamaDetails: PropTypes.object,
  accountDetails: PropTypes.object,
  healthInsuranceDetails: PropTypes.object,
  healthCardDetails: PropTypes.object,
  passportDetails: PropTypes.object,
  visaDetails: PropTypes.object,
  contractDetails: PropTypes.object,
  drivingLicenseDetails: PropTypes.object,
  subscriptionDetails: PropTypes.object,
  dependentsInfo: PropTypes.array,
  employeeGeneralInfo: PropTypes.object,
  empPublicId: PropTypes.string,
  getEmployeeRegistrationData: PropTypes.func,
  handleGetTableData: PropTypes.func,
};

export default EmployeeDetails;
